// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * CAS User information returned from the backend after successful authentication
 */
export interface CASUser {
  /** JWT token for API authentication */
  token: string;
  /** User's unique identifier */
  username: string;
  /** User's email address */
  email?: string;
  /** User's display name */
  name?: string;
  /** User's UMD directory ID */
  umid?: string;
  /** Token expiration timestamp (Unix timestamp in seconds) */
  exp?: number;
  /** Token issued at timestamp (Unix timestamp in seconds) */
  iat?: number;
  /** Additional user attributes from CAS */
  attributes?: Record<string, unknown>;
}

/**
 * Configuration options for CAS authentication
 */
export interface CASConfig {
  /** CAS server login URL (e.g., https://login.umd.edu/cas/login) */
  casLoginUrl: string;
  /** Your application's service URL that CAS will redirect back to */
  serviceUrl: string;
  /** Backend API endpoint that exchanges CAS tickets for JWTs */
  apiGatewayUrl: string;
  /** Optional: Storage key for JWT token (default: "cas_jwt") */
  storageKey?: string;
  /** Optional: Enable debug logging */
  debug?: boolean;
  /** Optional: Maximum redirect attempts before giving up (default: 3) */
  maxRedirects?: number;
  /** Optional: Callback to fetch additional user data after JWT exchange */
  onUserFetch?: (token: string, user: CASUser) => Promise<CASUser>;
  /** Optional: Callback when user is not found (404) in the system */
  onUserNotFound?: () => void;
  /** Optional: Enable automatic token refresh (default: true) */
  enableAutoRefresh?: boolean;
  /** Optional: Minutes before expiration to show warning (default: 10) */
  refreshWarningMinutes?: number;
  /** Optional: Seconds to wait after warning before auto-refresh (default: 30) */
  refreshWarningDurationSeconds?: number;
  /** Optional: Custom warning component (receives minutes until expiry and cancel callback) */
  refreshWarningComponent?: (props: RefreshWarningProps) => React.ReactNode;
}

/**
 * Authentication state
 */
export interface AuthState {
  /** Current authenticated user, null if not authenticated */
  user: CASUser | null;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Authentication error message if any */
  error: string | null;
}

/**
 * Local storage keys used by CAS auth
 */
export enum StorageKeys {
  JWT = "cas_jwt",
  USER = "cas_user",
  REDIRECT_PATH = "cas_redirect_path",
  REDIRECT_COUNT = "cas_redirect_count",
}

/**
 * Result of token validation
 */
export interface TokenValidationResult {
  valid: boolean;
  expired?: boolean;
  user?: CASUser;
  error?: string;
}

/**
 * Props for the refresh warning component
 */
export interface RefreshWarningProps {
  /** Minutes until token expires */
  minutesUntilExpiry: number;
  /** Seconds remaining until auto-refresh */
  secondsRemaining: number;
  /** Callback to refresh immediately */
  onRefreshNow: () => void;
  /** Callback to cancel auto-refresh */
  onCancel: () => void;
}